import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import {
  clearDealerAdminSession,
  readDealerAdminSession,
  writeDealerAdminSession,
} from '../pages/Admin/dealerAdminSession';
import { isOperatorShellRole } from '../utils/sessionRole';
import { resetAlgoAccess } from '../pages/Admin/algoDeviceSupport';

const DealerAdminContext = createContext(null);

export function DealerAdminProvider({ children }) {
  const { role } = useAuth();
  const [session, setSession] = useState(() => readDealerAdminSession());

  useEffect(() => {
    if (!isOperatorShellRole(role) || !session) return;
    clearDealerAdminSession();
    setSession(null);
  }, [role, session]);

  const enter = useCallback((dealerId, tenantId, extra = {}) => {
    const previous = readDealerAdminSession();
    const same = previous?.dealerId === dealerId && previous?.tenantId === tenantId;
    writeDealerAdminSession(dealerId, tenantId, extra);
    if (!same) resetAlgoAccess();
    setSession(readDealerAdminSession());
  }, []);

  const setAlgoAccess = useCallback((enabled) => {
    setSession((prev) => {
      if (!prev?.dealerId || !prev?.tenantId) return prev;
      writeDealerAdminSession(prev.dealerId, prev.tenantId, {
        tenantName: prev.tenantName,
        algoDeviceSupport: enabled === true,
      });
      return readDealerAdminSession();
    });
  }, []);

  const leave = useCallback(() => {
    clearDealerAdminSession();
    resetAlgoAccess();
    setSession(null);
  }, []);

  const value = useMemo(() => ({
    active: Boolean(session?.dealerId && session?.tenantId),
    dealerId: session?.dealerId || '',
    tenantId: session?.tenantId || '',
    tenantName: session?.tenantName || '',
    algoDeviceSupport: session?.algoDeviceSupport === true,
    enter,
    setAlgoAccess,
    leave,
  }), [enter, leave, session, setAlgoAccess]);

  return <DealerAdminContext.Provider value={value}>{children}</DealerAdminContext.Provider>;
}

export function useDealerAdmin() {
  const value = useContext(DealerAdminContext);
  if (!value) {
    return {
      active: false,
      dealerId: '',
      tenantId: '',
      tenantName: '',
      algoDeviceSupport: false,
      enter() {},
      setAlgoAccess() {},
      leave() {},
    };
  }
  return value;
}
